/**
 * Overview — Platform health snapshot
 */
import React, { useCallback } from "react";
import { metrics, workers, notifications } from "../api.js";
import { useAsync, useAutoRefresh } from "../hooks.js";

function statColor(value: number, warnAt: number, critAt: number): string {
  if (value >= critAt) return "var(--ops-danger)";
  if (value >= warnAt) return "var(--ops-warning)";
  return "var(--ops-success)";
}

function WorkerDot({ status }: { status: string }) {
  const cls =
    status === "online"   ? "ops-dot-green"  :
    status === "degraded" ? "ops-dot-amber"  :
    status === "offline"  ? "ops-dot-red"    : "ops-dot-grey";
  return <span className={`ops-dot ${cls}`} style={{ marginRight: 6 }} />;
}

export function OverviewView() {
  const overview  = useAsync(metrics.overview, []);
  const workersFn = useCallback(() => workers.all(), []);
  const workersD  = useAsync(workersFn, []);
  const notifFn   = useCallback(() => notifications.unreadCount(), []);
  const notifD    = useAsync(notifFn, []);

  useAutoRefresh(overview.reload, 30_000);
  useAutoRefresh(workersD.reload, 15_000);

  const d = overview.data as any;
  const w = (workersD.data as any)?.workers ?? [];

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">Platform Overview</h1>
          <p className="ops-page-subtitle">
            {d ? `As of ${new Date(d.asOf).toLocaleTimeString()}` : "Loading…"}
          </p>
        </div>
        {(notifD.data?.count ?? 0) > 0 && (
          <span className="ops-badge ops-badge-red">
            {notifD.data!.count} unread alert{notifD.data!.count !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {overview.error && <div className="ops-error">{overview.error}</div>}

      {/* Tenant stats */}
      <p className="ops-card-title" style={{ marginBottom: "0.5rem" }}>Tenants</p>
      <div className="ops-stat-grid" style={{ marginBottom: "1.5rem" }}>
        {[
          { label: "Total", value: d?.tenants?.total ?? "—" },
          { label: "Active", value: d?.tenants?.active ?? "—" },
          { label: "Trialing", value: d?.tenants?.trialing ?? "—", accent: "var(--ops-info)" },
          { label: "Paying", value: d?.tenants?.paying ?? "—", accent: "var(--ops-success)" },
          { label: "Suspended", value: d?.tenants?.suspended ?? "—", accent: d?.tenants?.suspended > 0 ? "var(--ops-warning)" : undefined },
        ].map(({ label, value, accent }) => (
          <div key={label} className="ops-stat">
            <div className="ops-stat-value" style={accent ? { color: accent } : {}}>{value}</div>
            <div className="ops-stat-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="ops-grid-2" style={{ gap: "1.25rem", marginBottom: "1.5rem" }}>
        {/* Publishing */}
        <div className="ops-card">
          <p className="ops-card-title">Publishing</p>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <div>
              <div className="ops-stat-value">{d?.publishing?.totalPublished ?? "—"}</div>
              <div className="ops-stat-label">Live sites</div>
            </div>
            <div>
              <div className="ops-stat-value">{d?.publishing?.publishedToday ?? "—"}</div>
              <div className="ops-stat-label">Published today</div>
            </div>
            {(d?.publishing?.failuresLast24h ?? 0) > 0 && (
              <div>
                <div className="ops-stat-value" style={{ color: "var(--ops-danger)" }}>
                  {d.publishing.failuresLast24h}
                </div>
                <div className="ops-stat-label">Failures (24h)</div>
              </div>
            )}
          </div>
        </div>

        {/* AI */}
        <div className="ops-card">
          <p className="ops-card-title">AI Usage — today</p>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <div>
              <div className="ops-stat-value">{d?.ai?.messagesToday ?? "—"}</div>
              <div className="ops-stat-label">Messages</div>
            </div>
            <div>
              <div className="ops-stat-value">{d?.ai?.generationsToday ?? "—"}</div>
              <div className="ops-stat-label">Generations</div>
            </div>
            <div>
              <div className="ops-stat-value">{d?.ai?.reviewsToday ?? "—"}</div>
              <div className="ops-stat-label">Reviews</div>
            </div>
          </div>
        </div>
      </div>

      {/* Worker health */}
      <div className="ops-card">
        <p className="ops-card-title">Worker Health</p>
        {workersD.loading && <div className="ops-loading">Probing workers…</div>}
        {w.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {w.map((worker: any) => (
              <div key={worker.name} className="ops-row">
                <WorkerDot status={worker.status} />
                <span style={{ fontWeight: 600, minWidth: 140 }}>{worker.displayName}</span>
                <span className="ops-badge" style={{
                  background: worker.status === "online" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                  color: worker.status === "online" ? "var(--ops-success)" : "var(--ops-danger)",
                }}>
                  {worker.status}
                </span>
                {worker.latencyMs != null && (
                  <span style={{ color: "var(--ops-muted)", fontSize: "0.75rem", marginLeft: "auto" }}>
                    {worker.latencyMs}ms
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {!workersD.loading && w.length === 0 && (
          <div className="ops-empty">Worker health endpoints not configured.</div>
        )}
      </div>
    </div>
  );
}
